import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalStore } from '../../stores/globalStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { recordPageVisit } from '../../api/feedbackApi';
import { useIsPsiStaff } from '../../utils/psiStaff';
import {
  installErrorCapture,
  recordBreadcrumb,
  registerStoreSnapshot,
} from '../../utils/feedbackContext';

/**
 * The passive half of Test Kitchen capture, mounted once for the whole app: window error
 * listeners, navigation breadcrumbs, store snapshot registration, and page-visit coverage pings.
 *
 * Renders nothing. The visible trigger is ReportProblemToolbarButton, which sits in the top
 * toolbar beside the notification bell. Keeping capture separate from the trigger means the
 * breadcrumb trail is already populated by the time a tester decides to report something.
 *
 * Gated on VITE_FEEDBACK_ENABLED so nothing is collected for real facilities before the beta,
 * and on a PSI login so a customer's navigation is never recorded.
 */
export default function FeedbackCapture() {
  const location = useLocation();
  const userId = useGlobalStore((s) => s.userId);
  const psi = useIsPsiStaff();

  const enabled = import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && psi;

  useEffect(() => {
    if (!enabled) return;
    return installErrorCapture();
  }, [enabled]);

  // Snapshot providers are registered rather than imported by the collector, so feedbackContext
  // stays free of store imports — importing stores there would cycle back through the API client.
  useEffect(() => {
    if (!enabled) return;

    const unregisterGlobal = registerStoreSnapshot('global', () => {
      const s = useGlobalStore.getState();
      return {
        userId: s.userId,
        userName: s.userName,
        facilityId: s.facilityId,
        facilityName: s.facilityName,
      };
    });

    const unregisterConnection = registerStoreSnapshot('connection', () => {
      const c = useConnectionStore.getState().selectedConnection;
      return c
        ? { connectionId: c.connectionId, facilityName: c.facilityName, webVersion: c.webVersion }
        : null;
    });

    return () => {
      unregisterGlobal();
      unregisterConnection();
    };
  }, [enabled]);

  // Breadcrumb + coverage ping on every navigation. Coverage is what the handbook actually judges
  // testers on: a screen with zero findings AND zero visits is unknown, not clean — and findings
  // alone cannot tell those two apart.
  useEffect(() => {
    if (!enabled) return;
    const route = location.pathname + (location.search || '');
    recordBreadcrumb('nav', route);
    if (userId > 0) recordPageVisit(userId, location.pathname);
  }, [enabled, location.pathname, location.search, userId]);

  // The visible trigger moved to the top toolbar (ReportProblemToolbarButton) — testers reach for
  // it where every other global action already lives, and a floating button collided with grid
  // scrollbars and the mobile tab bar. This component stays mounted for the passive half of
  // capture: window error listeners, navigation breadcrumbs, store snapshots, and coverage pings.
  return null;
}
